import { BackendApiClient } from './backend-client';
import { requireList } from '@/shared/lib/response-shape';
import { DebugLogger } from '@/shared/lib/debug';
import type { ScopeContext } from '../model/types';

const backendClient = new BackendApiClient();

const CACHE_KEY_PREFIX = 'veltiz-products';

/**
 * One resolve per page load per scope. A fulfilled entry is kept rather than dropped, because
 * the console mounts `SidebarContent` twice — the desktop column and the mobile drawer — and
 * the second mount asking again is a repeat, not a revalidation. A rejected entry is dropped
 * so the next mount retries. Reloading the page builds a new module instance, which is what
 * makes every load revalidate.
 */
const resolvedByKey = new Map<string, Promise<string[]>>();

/**
 * Raised when the product bindings could not be resolved. It is deliberately distinct from
 * resolving to an empty list: "we could not find out" must never reach a caller as "this
 * scope holds no products". `code` carries the backend's own error code so a caller can
 * tell a permanent refusal from a transient outage.
 */
export class ProductLookupError extends Error {
  readonly code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'ProductLookupError';
    this.code = code;
  }
}

/**
 * The backend error code an error carries, whatever kind it is. A failed request raises a
 * ProductLookupError and an unreadable body raises a BackendValidationError, and both name
 * the rule that was broken — reading only one of them logs `undefined` for the other, which
 * is the half of the failure a diagnosis usually needs.
 */
export function errorCodeOf(error: unknown): string | undefined {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : undefined;
}

function cacheKey(sessionId: string, scopeContext: ScopeContext): string {
  const holderId = scopeContext.accountHolder?.id ?? '';
  const accountId = scopeContext.account?.id ?? '';
  return `${CACHE_KEY_PREFIX}:${sessionId}:${holderId}:${accountId}`;
}

/**
 * The cache accelerates the first paint only. Every page load re-resolves from the source
 * (the two mounts within one load share that resolve, see below), so a
 * granted product and a revoked one both become visible on the next load rather than only
 * the first of the two.
 */
function readCache(key: string): string[] | null {
  try {
    const cached = sessionStorage.getItem(key);
    if (!cached) return null;
    const parsed: unknown = JSON.parse(cached);
    if (!Array.isArray(parsed) || parsed.some((id) => typeof id !== 'string')) return null;
    return parsed as string[];
  } catch {
    return null;
  }
}

function writeCache(key: string, productIds: string[]): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(productIds));
  } catch {
    return;
  }
}

function productIdsFrom(body: unknown): string[] {
  const bindings = requireList<unknown>(body, 'bindings', 'The product-binding response carried no readable bindings list');
  return bindings.flatMap((binding) =>
    requireList<string>(binding, 'product_ids', 'A product binding carried no readable product_ids list')
  );
}

async function fetchProductIdsForScope(
  sessionId: string,
  scopeId: string,
  scopeType: 'holder' | 'account'
): Promise<string[]> {
  const response = await backendClient.get('/product-binding', sessionId, { scopeId, scopeType });
  if (!response.success || !response.data) {
    throw new ProductLookupError(
      response.error ?? `Could not resolve products for ${scopeType} ${scopeId}`,
      response.errorCode
    );
  }
  return productIdsFrom(response.data);
}

/**
 * Every scope in the context must answer before the union is a fact. A leg that fails leaves
 * the products bound to that scope unknown, so the whole resolve fails rather than caching a
 * union that is short by an unknown amount.
 */
async function resolveProductIds(key: string, sessionId: string, scopeContext: ScopeContext): Promise<string[]> {
  const requests: Promise<string[]>[] = [];

  if (scopeContext.accountHolder) {
    requests.push(fetchProductIdsForScope(sessionId, scopeContext.accountHolder.id, 'holder'));
  }
  if (scopeContext.account) {
    requests.push(fetchProductIdsForScope(sessionId, scopeContext.account.id, 'account'));
  }

  if (requests.length === 0) {
    throw new ProductLookupError('Scope context names neither an account holder nor an account');
  }

  const results = await Promise.all(requests);
  const productIds = [...new Set(results.flat())];
  writeCache(key, productIds);
  return productIds;
}

/**
 * The last confirmed answer, for painting before the network answers. An empty entry never
 * paints — it is indistinguishable from a record left by the build that cached failures, and
 * a confirmed-empty answer painted early would flash the short menu this exists to prevent.
 * The resolve that follows supplies it a moment later either way, so the only cost is a
 * skeleton for accounts that genuinely hold nothing.
 */
export function getCachedProductIds(sessionId: string, scopeContext: ScopeContext): string[] | null {
  const cached = readCache(cacheKey(sessionId, scopeContext));
  return cached && cached.length > 0 ? cached : null;
}

export async function getProductIds(
  sessionId: string,
  scopeContext: ScopeContext,
  options: { refresh?: boolean } = {}
): Promise<string[]> {
  const key = cacheKey(sessionId, scopeContext);
  if (options.refresh) resolvedByKey.delete(key);

  const existing = resolvedByKey.get(key);
  if (existing) return existing;

  const promise = resolveProductIds(key, sessionId, scopeContext).catch((error) => {
    resolvedByKey.delete(key);
    throw error;
  });
  resolvedByKey.set(key, promise);
  return promise;
}

export function prefetchProductBindings(sessionId: string, scopeContext: ScopeContext): void {
  getProductIds(sessionId, scopeContext).catch((error) => {
    DebugLogger.error('Prefetching product bindings failed; the sidebar will resolve them again', {
      sessionId,
      error: String(error),
      code: errorCodeOf(error),
    });
  });
}
